import EventBus from 'components/event-bus';
import { TypographyH4 } from 'components/typography';
import type { UploadProgressMessage } from 'interfaces/app';
import * as PI from 'interfaces/project';
import * as DialogService from 'services/dialog';
import {
	AppStateModule,
	ConfigModule,
	PhotosModule,
	ProductStateModule,
	UIModule,
	UserModule,
} from 'store';
import { componentSlot } from 'store/ui';
import _ from 'underscore';
import {
	type Component,
	computed,
	reactive,
} from 'vue';
import type { Cons } from 'vue-facing-decorator/dist/component';
import {
	applyProgressUpdate,
	buildExternalUploadChannel,
	buildExternalUploadMutation,
	shouldOpenProgressDialog,
	type UploadProgressDialogProps,
} from './external-upload-helpers';
import type { UploadSessionMachineActor } from './session';

type AwsAppSync = typeof import('controllers/aws-appsync').default;

let cachedAwsAppSync: AwsAppSync | null = null;

async function loadAwsAppSync(): Promise<AwsAppSync> {
	if (cachedAwsAppSync === null) {
		cachedAwsAppSync = (await import('controllers/aws-appsync')).default;
	}

	return cachedAwsAppSync;
}

/**
 * Report a failed external-upload AppSync mutation to Bugsnag. These boundaries
 * are best-effort peer signalling: a rejection (network, missing credentials,
 * the dynamic import failing) must never escape and strand the progress dialog
 * open, so callers always run their UI continuation regardless. We still
 * surface the failure so silent peer-signalling breakage stays visible.
 */
function notifyExternalUploadError(
	err: unknown,
	event: 'abort' | 'close' | 'upload' | 'complete' | 'fetchPhotos',
): void {
	if (typeof window.glBugsnagClient === 'undefined') {
		return;
	}

	const error = err instanceof Error
		? err
		: new Error(`External upload ${event} mutation failed`);

	window.glBugsnagClient.notify(
		error,
		(reportedEvent) => {
			reportedEvent.severity = 'warning';
			reportedEvent.addMetadata(
				'externalUpload',
				{
					event,
				},
			);
		},
	);
}

export type ExternalUploadDialogComponents = {
	uploadProgress: Cons;
	uploadQr: Component;
};

export type ExternalUploadControllerOptions = {
	/** Returns the live root upload session actor. */
	getSession: () => UploadSessionMachineActor;
	/** Called when the user cancels via the in-progress dialog. */
	onCancelAll: () => void;
	dialogs: ExternalUploadDialogComponents;
};

const SHARE_PROGRESS_THROTTLE_MS = 200;

/**
 * Owns the external upload dialog stack (QR + progress) and the AppSync mutation
 * traffic that flows when external uploads (e.g. from a phone over QR) push
 * progress events into this session. The upload controller delegates to this
 * class so that the QR/progress dialog state never lives next to the file-pipe
 * machinery.
 */
export class ExternalUploadController {
	private uploadProgressDialogClose: (() => void) | undefined;

	private uploadProgressDialogProps: UploadProgressDialogProps | undefined;

	/**
	 * Latches when the user manually closes the progress dialog so that a
	 * subsequent `< 100%` progress tick does not reopen it. Cleared at the start
	 * of each fresh session: {@link showQRCode} for external uploads,
	 * {@link startLocalBatch} for local batches, and at session boundaries for
	 * AppSync-driven flows that bypass `showQRCode`.
	 */
	private progressDialogDismissed = false;

	private uploadQRDialogClose: (() => void) | undefined;

	private readonly shareProgressThrottled: ((data: UploadProgressMessage) => void)
		& { cancel: () => void };

	/**
	 * The most recent progress payload handed to {@link shareProgressThrottled},
	 * and the payload the throttle actually forwarded to the peer. When they
	 * diverge a trailing tick is still pending; {@link dispose} flushes it so a
	 * mid-progress teardown does not leave the peer hanging on a stale `< 100%`
	 * (or never-sent `100%`) state.
	 */
	private pendingShareData: UploadProgressMessage | undefined;

	private lastSentShareData: UploadProgressMessage | undefined;

	public constructor(private readonly options: ExternalUploadControllerOptions) {
		this.shareProgressThrottled = _.throttle(
			(data: UploadProgressMessage) => {
				this.lastSentShareData = data;
				void this.shareProgress(data);
			},
			SHARE_PROGRESS_THROTTLE_MS,
		);
	}

	/**
	 * Clears the dismissal latch at the start of a fresh local upload batch so
	 * a previously dismissed dialog can reopen for the new run. The latch only
	 * tracks intent for the active session; once the user submits a new batch
	 * that intent no longer applies.
	 */
	public startLocalBatch(): void {
		this.progressDialogDismissed = false;
	}

	/** Open the QR dialog for users to scan from their phone. */
	public showQRCode(): Promise<void> {
		/*
		 * A fresh external session resets the dismissal latch so a previously
		 * dismissed progress dialog can re-open for the new upload run.
		 */
		this.progressDialogDismissed = false;

		const closeLoaderDialog = DialogService.openLoaderDialog();
		const session = this.options.getSession();

		session.send({
			type: 'session.external.qr.requested',
		});

		return ProductStateModule
			.getQRCodeForExternalUpload()
			.then(({ image, url }) => {
				closeLoaderDialog();

				let closeDialog: (() => void) | undefined = undefined;
				/**
				 * User-initiated abort (close button / click-outside on the QR
				 * dialog). Tears the QR dialog down and reports the abort to the
				 * session machine before notifying the peer with the AppSync `abort`
				 * mutation, so slow peer signalling cannot strand the UI.
				 *
				 * Contrast with {@link closeQRDialog}, which is the
				 * peer-progress-arrived path: when the QR closes only because the
				 * peer is already uploading we must NOT abort it. {@link dispose}
				 * is the third path: programmatic teardown that also aborts.
				 */
				const abortAndCloseDialog = (): void => {
					closeDialog?.();
					this.uploadQRDialogClose = undefined;
					session.send({
						type: 'session.external.qr.aborted',
					});
					void this.abortPeer();
				};

				this.uploadQRDialogClose = () => {
					closeDialog?.();
					this.uploadQRDialogClose = undefined;
				};

				closeDialog = UIModule.openDialog({
					header: componentSlot({
						tag: TypographyH4,
						slot: window.App.router.$t('views.uploadQr.header'),
					}),
					body: componentSlot({
						tag: this.options.dialogs.uploadQr,
						props: {
							image,
							link: url,
						},
					}),
					props: {
						class: 'md:w-115',
						onClose: abortAndCloseDialog,
						'onClick:outside': abortAndCloseDialog,
					},
				});

				session.send({
					type: 'session.external.qr.shown',
				});
			})
			.catch((err) => {
				session.send({
					type: 'session.external.qr.failed',
				});
				closeLoaderDialog();
				DialogService.openErrorDialog({
					body: {
						content: err.message,
					},
				});
			});
	}

	/**
	 * Update the progress dialog state. Called every tick from the upload
	 * controller's internal poll loop, and on every AppSync progress event for
	 * external uploads.
	 *
	 * The `fromExternalUpload` flag distinguishes the two sources: external
	 * progress also drives the session machine's external child; local progress
	 * tears down the QR dialog the first time it arrives.
	 */
	public updateProgress(
		data: UploadProgressMessage,
		fromExternalUpload: boolean,
	): void {
		if (AppStateModule.uploadOnly) {
			this.pendingShareData = data;
			this.shareProgressThrottled(data);
		} else if (!fromExternalUpload && this.uploadQRDialogClose) {
			this.closeQRDialog();
		}

		if (fromExternalUpload) {
			/*
			 * Clear the dismissal latch when no external session is in flight.
			 * AppSync-driven external uploads can hit updateProgress without ever
			 * going through showQRCode (e.g. the user accepted the QR on a peer
			 * device); a previous session's dismissed latch must not block this
			 * new session's dialog. The external child sits in `idle` or a
			 * `terminal`-tagged state (`completed` / `aborted`) when no session
			 * is in flight — either signals "next progress event is a new
			 * session boundary".
			 */
			/*
			 * Implicit shared contract: this latch-clearing reads the external
			 * child snapshot BEFORE forwarding the progress event, and so does
			 * `forwardExternalEvent` in session.ts (the `startsAfterTerminalSession`
			 * / `submittedCountChanged` batch-cleanup branch) when it independently
			 * re-reads the same snapshot to detect a session boundary. Both rely on
			 * the pre-event snapshot; the order here (read, then send) must not be
			 * flipped, or session.ts would observe a snapshot already advanced by
			 * this same event and the two boundary detections would disagree.
			 */
			const externalSnapshot = this.options.getSession().getSnapshot().context.externalRef.getSnapshot();

			if (externalSnapshot.matches('idle') || externalSnapshot.hasTag('terminal')) {
				this.progressDialogDismissed = false;
			}

			this.options.getSession().send({
				type: 'session.external.progress.received',
				data,
			});
		}

		if (this.uploadProgressDialogClose && this.uploadProgressDialogProps) {
			applyProgressUpdate(
				this.uploadProgressDialogProps,
				data,
			);
			return;
		}

		const shouldOpen = shouldOpenProgressDialog(
			data,
			{
				isUploadOnly: AppStateModule.uploadOnly,
				isFromExternalUpload: fromExternalUpload,
				isDialogOpen: Boolean(this.uploadProgressDialogClose),
				wasDismissed: this.progressDialogDismissed,
			},
		);

		if (shouldOpen) {
			this.openProgressDialog(data);
		}
	}

	/** Called by the progress dialog's onStartEditing action. */
	public externalUploadsReady(): void {
		const session = this.options.getSession();
		const externalSnapshot = session.getSnapshot().context.externalRef.getSnapshot();
		const photoIds: (number | string)[] = externalSnapshot.context.photos.map(
			(photo) => photo.id,
		);

		session.send({
			type: 'session.external.editing.started',
		});

		/*
		 * Track the id behind each fetch so we can drop ids whose fetchModel
		 * rejected: an unfetched id would otherwise flow into addPhotos as a
		 * phantom and `external upload completed` would fire as if every photo
		 * had succeeded. Ids already present in the store need no fetch and are
		 * always kept.
		 */
		const fetchedIds: number[] = [];
		const fetchPromises: Promise<PI.PhotoModel>[] = [];

		photoIds.forEach((photoId) => {
			const photoModel = PhotosModule.getById(photoId);

			if (!photoModel && typeof photoId === 'number') {
				fetchedIds.push(photoId);
				fetchPromises.push(PhotosModule.fetchModel({
					id: photoId,
				}));
			}
		});

		Promise
			.allSettled(fetchPromises)
			.then((results) => {
				const failedIds = new Set<number>();

				results.forEach((result, index) => {
					if (result.status === 'rejected') {
						const failedId = fetchedIds[index];
						failedIds.add(failedId);
						notifyExternalUploadError(
							result.reason,
							'fetchPhotos',
						);
					}
				});

				const readyIds = photoIds.filter(
					(photoId) => typeof photoId !== 'number' || !failedIds.has(photoId),
				);

				ProductStateModule.addPhotos(readyIds);
				this.closeProgressDialog();

				EventBus.emit(
					'external upload completed',
					readyIds,
				);
			});
	}

	/**
	 * Tear down both dialogs as part of programmatic teardown (e.g.
	 * `controller.reset()`). Unlike {@link closeQRDialog} (the
	 * peer-progress-arrived path, which must stay silent so an actively
	 * uploading phone is not interrupted), dispose notifies the peer with the
	 * AppSync `abort` mutation whenever a QR dialog is still open, so the phone
	 * session does not keep uploading into a now-dead controller. Any pending
	 * throttled progress share is flushed first so the peer-facing state is not
	 * left a tick behind before the abort lands.
	 *
	 * The dismissed latch is NOT cleared here — `dispose()` is invoked from
	 * `controller.reset()` which itself is sometimes triggered by the user
	 * clicking cancel on the progress dialog. Clearing the latch on dispose
	 * would cause a later sub-100% external progress tick to re-open the
	 * dialog the user just dismissed. The latch is cleared explicitly when a
	 * fresh external upload session begins — either via {@link showQRCode} or
	 * detected inside {@link updateProgress} via the external child's state.
	 */
	public dispose(): void {
		this.flushPendingShare();

		if (this.uploadQRDialogClose) {
			void this.abortPeer();
		}

		this.closeProgressDialog();
		this.closeQRDialog();
	}

	/**
	 * Flush any progress tick the throttle has not yet forwarded. Underscore's
	 * `_.throttle` only exposes `.cancel()` (no lodash-style `.flush()`), so the
	 * pending trailing edge is cancelled and the latest payload is sent
	 * explicitly when it differs from what the throttle last forwarded.
	 */
	private flushPendingShare(): void {
		this.shareProgressThrottled.cancel();

		if (
			this.pendingShareData
			&& this.pendingShareData !== this.lastSentShareData
		) {
			const data = this.pendingShareData;
			this.lastSentShareData = data;
			void this.shareProgress(data);
		}
	}

	/**
	 * Send the AppSync `abort` mutation to the peer (if a channel exists),
	 * notifying that this controller is tearing down so the phone session stops
	 * uploading. A `loadAwsAppSync`/`createMutation` rejection is reported to
	 * Bugsnag rather than escaping as an unhandled rejection — the caller's UI
	 * teardown must still run regardless of whether the peer was reachable.
	 */
	private async abortPeer(): Promise<void> {
		const channel = this.externalUploadChannel();

		if (!channel) {
			return;
		}

		try {
			const appSync = await loadAwsAppSync();
			await appSync.createMutation(buildExternalUploadMutation(
				channel,
				String(ConfigModule['appSync.url']),
				'abort',
				{},
			));
		} catch (err) {
			notifyExternalUploadError(
				err,
				'abort',
			);
		}
	}

	/**
	 * Send the AppSync `close` mutation to the peer (if a channel exists),
	 * signalling that the desktop side has dismissed the progress dialog or
	 * moved on to editing. Like {@link abortPeer}, a rejection is reported to
	 * Bugsnag rather than escaping — the caller's UI continuation
	 * (closeProgressDialog / externalUploadsReady) must always run so the dialog
	 * cannot get stuck open when AppSync is unreachable.
	 */
	private async closePeer(): Promise<void> {
		const channel = this.externalUploadChannel();

		if (!channel) {
			return;
		}

		try {
			const appSync = await loadAwsAppSync();
			await appSync.createMutation(buildExternalUploadMutation(
				channel,
				String(ConfigModule['appSync.url']),
				'close',
				{},
			));
		} catch (err) {
			notifyExternalUploadError(
				err,
				'close',
			);
		}
	}

	/** Close the QR dialog (no AppSync abort). */
	public closeQRDialog(): void {
		this.uploadQRDialogClose?.();
		this.uploadQRDialogClose = undefined;
	}

	/** Close the upload-progress dialog without firing AppSync `close`. */
	public closeProgressDialog(): void {
		this.uploadProgressDialogClose?.();
		this.uploadProgressDialogProps = undefined;
		this.uploadProgressDialogClose = undefined;
	}

	private openProgressDialog(data: UploadProgressMessage): void {
		/*
		 * Seed through the same sanitize helpers every later tick uses
		 * (applyProgressUpdate) so the initial dialog state can never carry a
		 * raw, untrusted value (e.g. a negative percentage from a hostile peer)
		 * that the next tick would then silently correct.
		 */
		this.uploadProgressDialogProps = reactive<UploadProgressDialogProps>({
			progressPercentage: 0,
			submittedCount: 0,
			photos: [],
			standalone: AppStateModule.uploadOnly,
		});
		applyProgressUpdate(
			this.uploadProgressDialogProps,
			data,
		);

		this.uploadProgressDialogClose = UIModule.openDialog({
			body: componentSlot<Cons>({
				tag: this.options.dialogs.uploadProgress,
				props: computed(() => ({
					...this.uploadProgressDialogProps!,
					onCancel: () => {
						/*
						 * onCancelAll routes through controller.reset → externalUpload.dispose
						 * → closeProgressDialog which already closes this specific dialog via
						 * the stored UIModule handle. Avoid an extra UIModule.closeDialog()
						 * pop that would otherwise dismiss whatever dialog now sits on top.
						 */
						this.progressDialogDismissed = true;
						this.options.onCancelAll();
					},
					onClose: () => {
						this.progressDialogDismissed = true;
						this.closeProgressDialog();
						void this.closePeer();
					},
					onStartEditing: () => {
						this.externalUploadsReady();
						void this.closePeer();
					},
				})),
			}),
			props: {
				class: 'md:w-180',
				hasCloseButton: false,
			},
		});
	}

	private async shareProgress(data: UploadProgressMessage): Promise<void> {
		const channel = this.externalUploadChannel();

		if (!channel) {
			return;
		}

		const url = String(ConfigModule['appSync.url']);

		try {
			const appSync = await loadAwsAppSync();

			await appSync.createMutation(buildExternalUploadMutation(
				channel,
				url,
				'upload',
				data,
			));

			if (data.progressPercentage >= 100) {
				await appSync.createMutation(buildExternalUploadMutation(
					channel,
					url,
					'complete',
					data,
				));
			}
		} catch (err) {
			/*
			 * Progress sharing is best-effort peer signalling driven from a
			 * fire-and-forget throttle; a rejection here has no UI continuation
			 * to protect but must not surface as an unhandled rejection.
			 */
			notifyExternalUploadError(
				err,
				data.progressPercentage >= 100 ? 'complete' : 'upload',
			);
		}
	}

	private externalUploadChannel(): string | null {
		const productId = ProductStateModule.getProductId;
		const userId = UserModule.id;

		if (!productId || !userId) {
			return null;
		}

		return buildExternalUploadChannel(
			productId,
			userId,
		);
	}
}
